// ── Cognito Authentication Module ────────────────────────────────────────
// Handles Cognito User Pool integration for Midway-federated sign-in.
// Gracefully degrades when Cognito is not configured (env vars not set).

// Lazy import to avoid crashing when the package's Node.js dependencies
// (buffer, crypto) aren't polyfilled in the browser environment.
type CognitoUserPoolType = import('amazon-cognito-identity-js').CognitoUserPool
type CognitoUserType = import('amazon-cognito-identity-js').CognitoUser
type CognitoUserSessionType = import('amazon-cognito-identity-js').CognitoUserSession

// ── Configuration ─────────────────────────────────────────────────────

interface CognitoConfig {
  userPoolId: string
  clientId: string
  domain: string
}

function loadConfig(): CognitoConfig | null {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || ''
  const domain = import.meta.env.VITE_COGNITO_DOMAIN || ''

  if (!userPoolId || !clientId) {
    console.info('[cognito-auth] Cognito not configured (missing VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_CLIENT_ID). Auth disabled.')
    return null
  }

  return { userPoolId, clientId, domain }
}

// ── Module State ──────────────────────────────────────────────────────

let userPool: CognitoUserPoolType | null = null
let config: CognitoConfig | null = null
let initialized = false
let initFailed = false

// ── Initialization ────────────────────────────────────────────────────

async function ensureInitialized(): Promise<void> {
  if (initialized) return
  initialized = true
  config = loadConfig()
  if (config) {
    try {
      const { CognitoUserPool } = await import('amazon-cognito-identity-js')
      userPool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.clientId,
      })
    } catch (err) {
      console.warn('[cognito-auth] Failed to load amazon-cognito-identity-js:', err)
      initFailed = true
      userPool = null
    }
  }
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Returns true if Cognito is configured and available.
 */
export function isCognitoConfigured(): boolean {
  // Synchronous check - only returns true if already initialized successfully
  if (!initialized) {
    config = loadConfig()
    return config !== null
  }
  return userPool !== null
}

/**
 * Gets the current authenticated Cognito user session, or null if not signed in.
 * Automatically refreshes the token if expired.
 */
export async function getCurrentSession(): Promise<CognitoUserSessionType | null> {
  await ensureInitialized()
  if (!userPool) return null

  const currentUser = userPool.getCurrentUser()
  if (!currentUser) {
    console.info('[cognito-auth] No current user in pool')
    return null
  }

  return new Promise((resolve) => {
    // getSession() automatically refreshes expired tokens using the refresh token
    currentUser.getSession((err: Error | null, session: CognitoUserSessionType | null) => {
      if (err) {
        console.info('[cognito-auth] getSession error:', err.message)
        resolve(null)
        return
      }
      if (!session) {
        console.info('[cognito-auth] No session returned')
        resolve(null)
        return
      }
      if (!session.isValid()) {
        console.info('[cognito-auth] Session invalid after refresh attempt')
        resolve(null)
        return
      }
      resolve(session)
    })
  })
}

/**
 * Gets the current JWT ID token string, or null if not authenticated.
 * This is the token to include in Authorization headers.
 */
export async function getJwtToken(): Promise<string | null> {
  const session = await getCurrentSession()
  if (!session) return null
  return session.getIdToken().getJwtToken()
}

/**
 * Gets the authenticated user's alias/username, or null if not signed in.
 */
export async function getUserAlias(): Promise<string | null> {
  const session = await getCurrentSession()
  if (!session) return null

  const idToken = session.getIdToken()
  const payload = idToken.decodePayload()

  // Prefer given_name, then email alias, then clean up cognito:username
  return (
    payload['given_name'] ||
    payload['name'] ||
    payload['email']?.split('@')[0] ||
    payload['preferred_username'] ||
    payload['cognito:username']?.replace(/^[^_]+_/, '').split('@')[0] ||
    null
  )
}

/**
 * Gets the current CognitoUser object, or null if not signed in.
 */
export function getCurrentUser(): CognitoUserType | null {
  // Synchronous - only works after ensureInitialized has been awaited elsewhere
  if (!userPool) return null
  return userPool.getCurrentUser()
}

/**
 * Authenticates a user directly using SRP (no hosted UI redirect).
 * Handles NEW_PASSWORD_REQUIRED challenge for first-time admin-created users.
 */
export async function authenticateUser(
  username: string,
  password: string,
  newPassword?: string,
): Promise<{ challenge?: 'NEW_PASSWORD_REQUIRED' }> {
  await ensureInitialized()
  if (!userPool) throw new Error('Cognito not configured')

  const { CognitoUser, AuthenticationDetails } = await import('amazon-cognito-identity-js')

  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  })

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  })

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (_session: CognitoUserSessionType) => {
        console.info('[cognito-auth] SRP authentication successful')
        resolve({})
      },
      onFailure: (err: Error) => {
        console.warn('[cognito-auth] SRP authentication failed:', err.message)
        if (err.name === 'NotAuthorizedException') {
          reject(new Error('Incorrect email or password'))
        } else if (err.name === 'UserNotFoundException') {
          reject(new Error('Incorrect email or password'))
        } else if (err.name === 'UserNotConfirmedException') {
          reject(new Error('Account not confirmed. Contact your administrator.'))
        } else {
          reject(new Error(err.message || 'Authentication failed'))
        }
      },
      newPasswordRequired: (userAttributes: Record<string, string>) => {
        if (newPassword) {
          // Complete the challenge with the new password
          // Remove non-writable attributes
          delete userAttributes.email_verified
          delete userAttributes.phone_number_verified
          cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
            onSuccess: (_session: CognitoUserSessionType) => {
              console.info('[cognito-auth] New password set successfully')
              resolve({})
            },
            onFailure: (err: Error) => {
              reject(new Error(err.message || 'Failed to set new password'))
            },
          })
        } else {
          resolve({ challenge: 'NEW_PASSWORD_REQUIRED' })
        }
      },
    })
  })
}

/**
 * Initiates sign-in. With custom UI, this is a no-op - the SignInPage
 * component calls authenticateUser() directly. Kept for backward
 * compatibility with the top nav "Sign in" button.
 */
export function signIn(): void {
  // No-op - sign-in is handled by the SignInPage component
  // The App component should show the SignInPage when not authenticated
  console.info('[cognito-auth] signIn() called - use SignInPage component for custom UI')
}

/**
 * Initiates federated sign-in via the Cognito Hosted UI with a specific IdP.
 * Redirects the browser to the Cognito authorize endpoint with identity_provider param.
 */
export function signInWithFederatedIdp(providerName: string): void {
  if (!config) config = loadConfig()
  if (!config || !config.domain) {
    console.warn('[cognito-auth] Cannot federate: Cognito domain not configured (VITE_COGNITO_DOMAIN)')
    return
  }

  const redirectUri = window.location.origin + '/'
  const url =
    `https://${config.domain}/oauth2/authorize?` +
    `identity_provider=${encodeURIComponent(providerName)}` +
    `&client_id=${config.clientId}` +
    `&response_type=code` +
    `&scope=openid+email+profile` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}`

  window.location.href = url
}

/**
 * Signs out the current user locally (clears session from browser storage).
 */
export function signOut(globalSignOut = false): void {
  const currentUser = getCurrentUser()
  if (!currentUser) return

  if (globalSignOut) {
    currentUser.globalSignOut({
      onSuccess: () => {
        console.info('[cognito-auth] Global sign-out successful')
      },
      onFailure: (err) => {
        console.warn('[cognito-auth] Global sign-out failed, doing local sign-out:', err.message)
        currentUser.signOut()
      },
    })
  } else {
    currentUser.signOut()
  }
}

/**
 * Attempts to silently refresh the current session tokens.
 * Returns true if refresh succeeded, false otherwise.
 */
export async function refreshSession(): Promise<boolean> {
  await ensureInitialized()
  if (!userPool) return false

  const currentUser = userPool.getCurrentUser()
  if (!currentUser) return false

  const session = await new Promise<CognitoUserSessionType | null>((resolve) => {
    currentUser.getSession((err: Error | null, sess: CognitoUserSessionType | null) => {
      if (err || !sess) {
        resolve(null)
        return
      }
      resolve(sess)
    })
  })

  if (!session) return false

  const refreshToken = session.getRefreshToken()
  if (!refreshToken) return false

  return new Promise((resolve) => {
    currentUser.refreshSession(refreshToken, (err: Error | null, newSession: CognitoUserSessionType | null) => {
      if (err || !newSession) {
        console.warn('[cognito-auth] Token refresh failed:', err?.message)
        resolve(false)
        return
      }
      console.info('[cognito-auth] Token refreshed successfully')
      resolve(true)
    })
  })
}

/**
 * Handles the OAuth callback after Cognito Hosted UI redirect.
 * Parses the authorization code from the URL and exchanges it for tokens.
 * Returns true if callback was handled, false if no code was present.
 */
export async function handleAuthCallback(): Promise<boolean> {
  await ensureInitialized()
  if (!config || !userPool) return false

  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  if (!code) return false

  // Exchange authorization code for tokens via the token endpoint
  const redirectUri = window.location.origin + '/'
  const tokenUrl = `https://${config.domain}/oauth2/token`

  try {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code,
        redirect_uri: redirectUri,
      }).toString(),
    })

    if (!response.ok) {
      console.error('[cognito-auth] Token exchange failed:', response.status)
      return false
    }

    const tokens = await response.json()

    // Create a CognitoUser session from the tokens
    const { CognitoIdToken, CognitoAccessToken, CognitoRefreshToken, CognitoUserSession, CognitoUser } = await import('amazon-cognito-identity-js')
    const idToken = new CognitoIdToken({ IdToken: tokens.id_token })
    const accessToken = new CognitoAccessToken({ AccessToken: tokens.access_token })
    const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token })

    const session = new CognitoUserSession({
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
    })

    // Extract username from ID token
    const payload = idToken.decodePayload()
    const username = payload['cognito:username'] || payload['sub']

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    cognitoUser.setSignInUserSession(session)

    // Clean up the URL (remove code param)
    const cleanUrl = window.location.origin + window.location.pathname
    window.history.replaceState({}, '', cleanUrl)

    console.info('[cognito-auth] Auth callback handled successfully')
    return true
  } catch (err) {
    console.error('[cognito-auth] Auth callback error:', err)
    return false
  }
}
